import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceContractsService } from '../services/contracts.service';
import type { MaintenanceContract, ContractFilters } from '../types/contracts.types';
import { useState } from 'react';

export function useMaintenanceContracts(companyId: string, initialFilters: ContractFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ContractFilters>({
    page: 1,
    pageSize: 10,
    status: 'all',
    ...initialFilters
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['maintenance-contracts', companyId, filters],
    queryFn: () => MaintenanceContractsService.getContracts(companyId, filters),
    enabled: !!companyId,
  });

  const { data: stats } = useQuery({
    queryKey: ['maintenance-contracts', 'stats', companyId, filters.type],
    queryFn: () => MaintenanceContractsService.getContractStats(companyId, filters.type),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceContract>) => MaintenanceContractsService.createContract(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-contracts'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<MaintenanceContract> }) => 
      MaintenanceContractsService.updateContract(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-contracts', 'detail', variables.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => MaintenanceContractsService.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-contracts'] });
    },
  });

  return {
    contracts: data?.data || [],
    total: data?.total || 0,
    stats: stats || { total: 0, active: 0, nearExpiry: 0, totalValue: 0 },
    isLoading,
    error,
    filters,
    setFilters,
    createContract: createMutation.mutateAsync,
    updateContract: updateMutation.mutateAsync,
    deleteContract: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export function useMaintenanceContractDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-contracts', 'detail', id],
    queryFn: () => MaintenanceContractsService.getContractDetail(id!),
    enabled: !!id,
  });
}

export function useContractScopes(contractId?: string) {
  return useQuery({
    queryKey: ['maintenance-contracts', 'scopes', contractId],
    queryFn: () => MaintenanceContractsService.getContractScopes(contractId!),
    enabled: !!contractId,
  });
}

export function useContractSLA(contractId?: string) {
  return useQuery({
    queryKey: ['maintenance-contracts', 'sla', contractId],
    queryFn: () => MaintenanceContractsService.getContractSLA(contractId!),
    enabled: !!contractId,
  });
}

export function useContractZones(contractId?: string) {
  return useQuery({
    queryKey: ['maintenance-contracts', 'zones', contractId],
    queryFn: () => MaintenanceContractsService.getContractZones(contractId!),
    enabled: !!contractId,
  });
}

export function useAssignContractZones() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contractId, companyId, zoneIds }: { contractId: string, companyId: string, zoneIds: string[] }) => 
      MaintenanceContractsService.assignZonesToContract(contractId, companyId, zoneIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-contracts', 'zones', variables.contractId] });
    },
  });
}

export function useRemoveContractZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, contractId }: { assignmentId: string, contractId: string }) => 
      MaintenanceContractsService.removeZoneFromContract(assignmentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-contracts', 'zones', variables.contractId] });
    },
  });
}
